import type { OrderStatus } from '@blackarrow/types';

/**
 * What a merchant is told about a parcel that came back undelivered.
 *
 * Internally a failed delivery is `declined` and then, once the rider is back
 * at the desk, `returned_to_hub`. Those are two steps of our own pipeline. To
 * the merchant they are one fact — the customer did not take it — so the
 * merchant portal shows both as "Declined" and keeps the internal split for
 * ops, where "who is holding it" matters.
 *
 * `returned_to_hub` is also the first hop of the cash tail on a delivered
 * parcel, but that hop happens in the same call as `cash_reconciled`
 * (OrderRemittanceService::reconcile), so a parcel does not rest there. The
 * only way to find one sitting there is a manual ops move, which is a mistake
 * to correct rather than a state to design for.
 *
 * The fold lives here, in the merchant routes, and not in `@blackarrow/types`:
 * ops shares those labels and needs the two statuses kept apart.
 */
export function merchantDisplayStatus(status: OrderStatus): OrderStatus {
    return status === 'returned_to_hub' ? 'declined' : status;
}

/**
 * The statuses the server should be asked for when the merchant filters by
 * these. Picking "Declined" means both of the statuses it stands for.
 */
export function expandStatusFilter(statuses: readonly OrderStatus[]): OrderStatus[] {
    const wantsDeclined = statuses.includes('declined') || statuses.includes('returned_to_hub');

    if (!wantsDeclined) return [...statuses];

    const rest = statuses.filter((s) => s !== 'declined' && s !== 'returned_to_hub');

    return [...rest, 'declined', 'returned_to_hub'];
}

/** The inverse, for the filter control: one "Declined" chip, never two. */
export function foldStatusFilter(statuses: readonly OrderStatus[]): OrderStatus[] {
    return statuses
        .map(merchantDisplayStatus)
        .filter((status, index, all) => all.indexOf(status) === index);
}

/**
 * The dashboard tiles counted by what happened today rather than where the
 * parcel is now (MerchantDashboard::today()), and the orders list's `happened`
 * values for the parcels behind them.
 */
export const MERCHANT_TODAY_TILES = ['delivered', 'declined', 'rescheduled'] as const;

export const MERCHANT_TODAY_LABELS: Record<(typeof MERCHANT_TODAY_TILES)[number], string> = {
    delivered: 'Delivered',
    declined: 'Declined',
    rescheduled: 'Rescheduled',
};
